import {
  ErrorMessage,
  InfoIcon,
  NumberField,
  Slider,
} from "@justhome/react-ui";
import { ReactElement, useId } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";

export interface InputWithSliderProps<T extends FieldValues> {
  label: string;
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
  suffix?: string;
  control: Control<T>;
  name: Path<T>;
  step?: number;
  marks?: boolean;
  format?: boolean;
  disabled?: boolean;
  appendix?: ReactElement;
  info?: string;
  germanLocale?: boolean;
  errorMessage?: string;
  description?: string;
}

const InputWithSlider = <T extends FieldValues>({
  label,
  min,
  max,
  suffix,
  minLabel,
  maxLabel,
  marks,
  format,
  disabled,
  appendix,
  info,
  germanLocale,
  errorMessage,
  description,
  step = 1,
  ...props
}: InputWithSliderProps<T>) => {
  const { field } = useController(props);
  const id = useId();
  const describedBy =
    [description && `${id}-description`, errorMessage && `${id}-error`]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div>
      <div className="flex items-center mb-xs">
        <div className="text-app-title-1 grow flex items-center gap-2xs">
          <label htmlFor={id}>{label}</label>
          {info && <InfoIcon label={label}>{info}</InfoIcon>}
        </div>
        <NumberField
          {...field}
          id={id}
          disabled={disabled}
          format={format}
          germanLocale={germanLocale}
          suffix={suffix}
          size="sm"
          className="max-w-[120px] flex-none ml-3xs"
          aria-invalid={!!errorMessage}
          aria-describedby={describedBy}
        />
        {appendix && <div className="ml-xs">{appendix}</div>}
      </div>
      {errorMessage && (
        <ErrorMessage id={`${id}-error`}>{errorMessage}</ErrorMessage>
      )}

      <Slider
        onChange={field.onChange}
        value={field.value}
        step={step}
        min={min}
        max={max}
        minLabel={minLabel}
        maxLabel={maxLabel}
        suffix={suffix}
        marks={marks}
        disabled={disabled}
        ariaLabel={label}
        ariaDescribedBy={describedBy}
        formatValue={(value) =>
          `${new Intl.NumberFormat(germanLocale === false ? "en" : "de").format(value)}${suffix ? ` ${suffix}` : ""}`
        }
      />
      {description && (
        <p
          id={`${id}-description`}
          className="mt-2xs text-body-2 text-neutral-425"
        >
          {description}
        </p>
      )}
    </div>
  );
};

export default InputWithSlider;
